import type { CPTCode } from "@/types/medical-codes";
import { Page } from "@/types/pagination";
import {
  CreateSuperbillRequest,
  SaveSuperbillRequest,
  SendSuperbillRequest,
  SuperbillChargeCandidate,
  SuperbillContext,
  SuperbillDetail,
  SuperbillEventCandidate,
  SuperbillPreviousDiagnosis,
  SuperbillSummary,
} from "@/types/superbill";

import { requestData } from "@/api/api-config";

export async function getSuperbillContext(
  chargeId: string,
): Promise<SuperbillContext | null> {
  return await requestData<SuperbillContext>(
    `billing/charge/${chargeId}/superbill-context`,
    "get",
  );
}

export async function saveSuperbill(
  chargeId: string,
  body: SaveSuperbillRequest,
): Promise<boolean | null> {
  return await requestData<boolean>(
    `billing/charge/${chargeId}/superbill`,
    "post",
    { json: body },
    false,
  );
}

export async function sendSuperbill(
  chargeId: string,
  body: SendSuperbillRequest,
): Promise<boolean | null> {
  return await requestData<boolean>(
    `billing/charge/${chargeId}/send-superbill`,
    "post",
    { json: body },
    false,
  );
}

// ---- Multi-charge Super Bill records ----

export async function listSuperbills(
  patientId: string,
): Promise<SuperbillSummary[] | null> {
  return await requestData<SuperbillSummary[]>(
    `billing/patient/${patientId}/superbills`,
    "get",
  );
}

export async function listSuperbillEventCandidates(
  patientId: string,
  opts?: { page?: number; perPage?: number },
): Promise<Page<SuperbillEventCandidate> | null> {
  // FastAPI expects snake_case query params.
  const searchParams: Record<string, string | number> = {};
  if (opts?.page) searchParams.page = opts.page;
  if (opts?.perPage) searchParams.per_page = opts.perPage;
  return await requestData<Page<SuperbillEventCandidate>>(
    `billing/patient/${patientId}/superbill-event-candidates`,
    "get",
    { searchParams },
  );
}

export async function listSuperbillChargeCandidates(
  patientId: string,
  opts?: { eventIds?: string[]; eventId?: string | null; days?: number },
): Promise<SuperbillChargeCandidate[] | null> {
  // FastAPI expects snake_case query params — camelCase here was silently
  // dropped on the server, so the visit filter never applied.
  const searchParams: Record<string, string | number | string[]> = {};
  if (opts?.eventIds && opts.eventIds.length > 0) {
    searchParams.event_ids = opts.eventIds;
  }
  if (opts?.eventId) searchParams.event_id = opts.eventId;
  if (opts?.days) searchParams.days = opts.days;
  return await requestData<SuperbillChargeCandidate[]>(
    `billing/patient/${patientId}/superbill-charge-candidates`,
    "get",
    { searchParams },
  );
}

export async function getSuperbillByEvent(
  patientId: string,
  eventId: string,
): Promise<SuperbillSummary | null> {
  // catchError: false — a successful response returns null only when no bill
  // exists. If we let requestData swallow network errors into null too, a
  // transient failure would read as "no existing bill" and silently create a
  // duplicate. Throwing lets the caller surface an error instead.
  return await requestData<SuperbillSummary>(
    `billing/patient/${patientId}/superbills/by-event/${eventId}`,
    "get",
    {},
    false,
  );
}

export async function getSuperbillByNote(
  patientId: string,
  noteId: string,
): Promise<SuperbillSummary | null> {
  // catchError: false — like getSuperbillByEvent, a successful response returns
  // null only when the note links to no bill. Swallowing network errors into
  // null would misread a transient failure as "no existing bill" and create a
  // duplicate; throwing lets the caller surface an error instead.
  return await requestData<SuperbillSummary>(
    `billing/patient/${patientId}/superbills/by-note/${noteId}`,
    "get",
    {},
    false,
  );
}

export async function getSuperbillByCharge(
  patientId: string,
  chargeId: string,
): Promise<SuperbillSummary | null> {
  // catchError: false — like getSuperbillByEvent, a successful response returns
  // null only when no bill covers the charge. Swallowing a network error into
  // null would misread it as "no bill" and mint a duplicate; throwing lets the
  // caller surface an error instead.
  return await requestData<SuperbillSummary>(
    `billing/patient/${patientId}/superbills/by-charge/${chargeId}`,
    "get",
    {},
    false,
  );
}

export async function linkNoteToSuperbill(
  superbillId: string,
  noteId: string,
): Promise<boolean | null> {
  return await requestData<boolean>(
    `billing/superbill/${superbillId}/notes/${noteId}`,
    "post",
    {},
    false,
  );
}

export async function createSuperbill(
  patientId: string,
  body: CreateSuperbillRequest,
): Promise<SuperbillDetail | null> {
  return await requestData<SuperbillDetail>(
    `billing/patient/${patientId}/superbills`,
    "post",
    { json: body },
  );
}

/** Copy a bill's claim document onto a new bill linked to `noteId`. */
export async function duplicateSuperbill(
  superbillId: string,
  noteId: string,
): Promise<SuperbillSummary | null> {
  return await requestData<SuperbillSummary>(
    `billing/superbill/${superbillId}/duplicate`,
    "post",
    { json: { noteId } },
  );
}

export async function getSuperbill(
  superbillId: string,
): Promise<SuperbillDetail | null> {
  return await requestData<SuperbillDetail>(
    `billing/superbill/${superbillId}`,
    "get",
  );
}

export async function getSuperbillPreviousDiagnoses(
  superbillId: string,
): Promise<SuperbillPreviousDiagnosis[] | null> {
  return await requestData<SuperbillPreviousDiagnosis[]>(
    `billing/superbill/${superbillId}/previous-diagnoses`,
    "get",
  );
}

/** Mirrors MAX_FAVORITE_CPT_CODES in decoda/services/favorite_cpt_codes.py. */
export const MAX_SUPERBILL_FAVORITE_CPT_CODES = 50;

export const superbillFavoriteCptCodesQueryKey = [
  "superbill-favorite-cpt-codes",
] as const;

// Throws on an HTTP error (catchError=false). A failed read must not look like
// an empty list: the settings page would then full-replace from [] and delete
// the clinic's codes on its first save.
export async function getSuperbillFavoriteCptCodes(): Promise<
  CPTCode[] | null
> {
  return await requestData<CPTCode[]>(
    "billing/superbill-favorite-cpt-codes",
    "get",
    undefined,
    false,
  );
}

// Throws on an HTTP error (catchError=false) so the settings page's mutation
// can roll back its optimistic update and report the failure.
export async function setSuperbillFavoriteCptCodes(
  codes: string[],
): Promise<CPTCode[] | null> {
  return await requestData<CPTCode[]>(
    "billing/superbill-favorite-cpt-codes",
    "put",
    { json: { codes } },
    false,
  );
}

export async function saveSuperbillRecord(
  superbillId: string,
  body: SaveSuperbillRequest,
): Promise<SuperbillDetail | null> {
  return await requestData<SuperbillDetail>(
    `billing/superbill/${superbillId}`,
    "put",
    { json: body },
  );
}

export async function deleteSuperbill(
  superbillId: string,
): Promise<boolean | null> {
  return await requestData<boolean>(
    `billing/superbill/${superbillId}`,
    "delete",
    {},
    false,
  );
}
